import React, {useState} from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  IconButton,
  Grid,
  Typography
} from '@mui/material';
import {Container} from '@mui/system';
import Iconify from 'src/minimal-components/iconify';

interface Expense {
  id: number;
  category: string;
  amount: number;
}

const ExpenseManager: React.FC = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [newExpenseAmount, setNewExpenseAmount] = useState('');
  const [newExpenseCategory, setNewExpenseCategory] = useState('');
  const [displayCost, setDisplayCost] = useState(true);
  const [currency, setCurrency] = useState('USD');
  const [hoveredItemId, setHoveredItemId] = useState<number | null>(null);

  const addExpense = () => {
    if (!newExpenseCategory || !newExpenseAmount) return;
    const newExpense = {
      id: Date.now(),
      category: newExpenseCategory,
      amount: Number(newExpenseAmount)
    };
    setExpenses([...expenses, newExpense]);
    setNewExpenseAmount('');
    setNewExpenseCategory('');
  };

  const deleteExpense = (expenseId: number) => {
    setExpenses(expenses.filter(expense => expense.id !== expenseId));
  };

  const getTotalAmount = () => {
    return expenses.reduce((total, expense) => total + expense.amount, 0);
  };

  return (
    <Container
      sx={{
        backgroundColor: 'white',
        borderRadius: '8px',
        maxWidth: '100% !important',
        py: '3vh'
      }}>
      <Grid
        container
        spacing={2}
        sx={{margin: '0 !important', width: '100% !important'}}>
        <Grid item xs={12}>
          <FormControl sx={{width: '20%'}}>
            <Typography variant="h3" sx={{py: '2vh'}}>
              Choose Currency
            </Typography>
            <InputLabel
              id="currency-label"
              sx={{display: currency.length <= 0 ? 'flex' : 'none'}}>
              Choose Currency
            </InputLabel>
            <Select
              labelId="currency-label"
              value={currency}
              onChange={e => setCurrency(e.target.value)}>
              {/* List all available currencies */}
              <MenuItem value="USD">USD United States Dollars</MenuItem>
              {/* Add more currencies */}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} py="1vh">
          <FormControlLabel
            control={
              <Checkbox
                checked={displayCost}
                onChange={e => setDisplayCost(e.target.checked)}
              />
            }
            label="Display the cost of the training to learners"
          />
        </Grid>
        <Grid
          container
          spacing={2}
          alignItems="center"
          sx={{
            borderBottom: '1px solid #edeff3',
            pb: '1vh',
            justifyContent: 'space-between',
            margin: '0 !important',
            width: '100% !important'
          }}>
          <Grid item xs={3}>
            <Typography variant="h4">Expense category</Typography>
          </Grid>
          <Grid item xs={3} sx={{paddingRight: '1vh'}}>
            <Typography variant="h4">Expense</Typography>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{display: 'flex', justifyContent: 'space-between'}}>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <InputLabel id="expense-category-label">
                Expense category
              </InputLabel>
              <Select
                labelId="expense-category-label"
                value={newExpenseCategory}
                onChange={e => setNewExpenseCategory(e.target.value)}
                label="Expense category">
                <MenuItem value="Trainers expenses">Trainers expenses</MenuItem>
                <MenuItem value="Registration expenses">
                  Registration expenses
                </MenuItem>
                {/* Add more categories as needed */}
              </Select>
            </FormControl>
          </Grid>
          <Grid
            item
            xs={3}
            sx={{display: 'flex', columnGap: '1vh', alignItems: 'center'}}>
            <TextField
              fullWidth
              label="Expense"
              value={newExpenseAmount}
              onChange={e => setNewExpenseAmount(e.target.value)}
              type="number"
            />
            <Button
              variant="contained"
              onClick={addExpense}
              sx={{height: 'fit-content'}}>
              Add
            </Button>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          alignItems="center"
          sx={{
            paddingTop: '2vh',
            margin: '0 !important',
            width: '100% !important'
          }}>
          {expenses.map(expense => (
            <React.Fragment key={expense.id}>
              <Grid
                item
                xs={12}
                sx={{
                  justifyContent: 'space-between',
                  pb: '2vh',
                  display: 'flex',
                  width: '100% !important',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
                onMouseEnter={() => setHoveredItemId(expense.id)}
                onMouseLeave={() => setHoveredItemId(null)}>
                <Grid
                  item
                  xs={3}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center'
                  }}>
                  <p>{expense.category}</p>
                </Grid>
                <Grid item xs={3}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      columnGap: '2vh'
                    }}>
                    <p>{`${currency} ${expense.amount}`}</p>
                    <IconButton
                      onClick={() => deleteExpense(expense.id)}
                      sx={{
                        color: 'error.main',
                        opacity: hoveredItemId === expense.id ? 1 : 0
                      }}>
                      <Iconify icon="solar:trash-bin-trash-bold" />
                    </IconButton>
                  </div>
                </Grid>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>

        <Grid
          item
          xs={12}
          sx={{display: 'flex', justifyContent: 'space-between'}}>
          <Grid item xs={2} sx={{display: 'flex'}}>
            <Typography variant="h6">TOTAL</Typography>
          </Grid>
          <Grid
            item
            xs={3}
            sx={{paddingLeft: '1vh', borderTop: '1px solid #edeff3'}}>
            <Typography
              variant="h4"
              style={{
                backgroundColor: 'lightblue',
                padding: '10px',
                textAlign: 'center'
              }}>
              {currency} {getTotalAmount()}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ExpenseManager;
